// Design-system primitives matching the Figma components (SCREEN-SPECS.md).
import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { VersionStatus } from "@bounce/core";
import { Icon, type IconName } from "./icons";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");
export { cx };

// ---------------------------------------------------------------- Button
type ButtonStyle = "primary" | "secondary" | "ghost";
const BTN_STYLE: Record<ButtonStyle, string> = {
  primary: "bg-accent text-on-brand hover:bg-accent-hover hover:shadow-glow-brand disabled:bg-overlay disabled:text-fg-disabled disabled:shadow-none",
  secondary: "bg-overlay text-fg border border-line-default hover:bg-hover disabled:border-line disabled:text-fg-disabled",
  ghost: "text-fg hover:bg-hover disabled:text-fg-disabled",
};
export function buttonClass(style: ButtonStyle = "primary", size: "sm" | "md" = "md") {
  return cx(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-full transition-[background-color,box-shadow] duration-150 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap",
    size === "sm" ? "px-4 py-2 type-label-m" : "px-6 py-3 type-label-l",
    BTN_STYLE[style],
  );
}
export function Button({
  variant = "primary",
  size = "md",
  icon,
  children,
  className,
  ...rest
}: { variant?: ButtonStyle; size?: "sm" | "md"; icon?: IconName } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={cx(buttonClass(variant, size), className)} {...rest}>
      {icon && <Icon name={icon} size={size === "sm" ? 16 : 20} />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- Status chip
export const STATUS_META: Record<VersionStatus, { label: string; color: string }> = {
  IDEA: { label: "Idea", color: "var(--color-status-idea)" },
  WRITING: { label: "Writing", color: "var(--color-status-writing)" },
  RECORDING: { label: "Recording", color: "var(--color-status-recording)" },
  MIXING: { label: "Mixing", color: "var(--color-status-mixing)" },
  MASTERING: { label: "Mastering", color: "var(--color-status-mastering)" },
  CHANGES_REQUESTED: { label: "Changes requested", color: "var(--color-status-changes)" },
  APPROVED: { label: "Approved", color: "var(--color-status-approved)" },
  RELEASED: { label: "Released", color: "var(--color-status-released)" },
};
export function StatusChip({ status, className }: { status: VersionStatus; className?: string }) {
  const m = STATUS_META[status];
  return (
    <span
      className={cx("inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-overlay px-2 py-1 type-label-s", className)}
      style={{ color: m.color, borderColor: m.color }}
    >
      <span className="size-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

// ---------------------------------------------------------------- Avatar
const AV = { S: "size-6 type-label-s", M: "size-8 type-label-m", L: "size-10 type-label-l" } as const;
export function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "?") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}
export function Avatar({ name, size = "M", className }: { name: string; size?: keyof typeof AV; className?: string }) {
  return (
    <span
      title={name}
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-brand text-on-brand ring-2 ring-canvas !normal-case",
        AV[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

// ---------------------------------------------------------------- Version tab
export function VersionTab({
  number,
  date,
  selected,
  href,
  onClick,
  processing,
}: {
  number: number;
  date: string;
  selected?: boolean;
  href?: string;
  onClick?: () => void;
  processing?: boolean;
}) {
  const inner = (
    <>
      <span className={cx("type-label-l", selected ? "text-fg" : "text-fg-2")}>
        v{number}
        {processing && <span className="ml-1 inline-block size-1.5 animate-pulse rounded-full bg-fg-brand align-middle" />}
      </span>
      <span className="type-body-s text-fg-3">{date}</span>
    </>
  );
  const cls = cx(
    "flex flex-col gap-0.5 rounded-md px-4 py-2 text-left transition-colors",
    selected ? "bg-raised border-b-2 border-accent" : "hover:bg-hover/60 border-b-2 border-transparent",
  );
  return href ? (
    <Link href={href} className={cls} scroll={false} aria-current={selected ? "page" : undefined}>
      {inner}
    </Link>
  ) : (
    <button className={cls} onClick={onClick} aria-pressed={selected}>
      {inner}
    </button>
  );
}

// ---------------------------------------------------------------- Pill / Card / misc
export function Pill({ children, active, onClick, className }: { children: ReactNode; active?: boolean; onClick?: () => void; className?: string }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 type-label-m transition-colors",
        active ? "bg-hover border-line-strong text-fg" : "bg-raised border-line-default text-fg-2 hover:text-fg",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cx("rounded-xl border border-line bg-surface px-6 py-5", className)}>{children}</section>;
}

/** Gradient cover art placeholder (deterministic per id) until real covers exist. */
const GRADS = [
  ["#F52BA6", "#A04FD8"],
  ["#A04FD8", "#0EBEFA"],
  ["#FFB020", "#F52BA6"],
  ["#0EBEFA", "#2BD98C"],
  ["#F52BA6", "#0EBEFA"],
];
export function coverGradient(id: string) {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const [a, b] = GRADS[Math.abs(h) % GRADS.length]!;
  return `linear-gradient(135deg, ${a}, ${b})`;
}
export function Cover({ id, size, radius = 8, children, className }: { id: string; size: number; radius?: number; children?: ReactNode; className?: string }) {
  return (
    <div
      className={cx("flex shrink-0 items-center justify-center", className)}
      style={{ width: size, height: size, borderRadius: radius, background: coverGradient(id) }}
    >
      {children}
    </div>
  );
}

export function relativeDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
